import type { StaffRouter } from '@eklevo/api/staff-trpc'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpLink } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import { needsReauthentication, sessionGone } from '../../lib/errors'
import { recordActivity } from './activity'
import { endStaffSession } from './session-end'

/*
 * The back office's API client (ADR 0008 §4): its own operations only, at /api/staff/trpc on
 * its own host. Every operation goes by POST, reads included, and alone (each one leaves its own
 * entry in the audit trail, so the server takes no batches).
 */

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<StaffRouter>()

export const STAFF_API = '/api/staff/trpc'

/** The key of auth.state in the query cache (`trpc.auth.state.pathKey()`). */
export const STAFF_STATE_KEY = [['auth', 'state']]

/** Operations the server does not count as activity: a page may ask for them on a timer. */
const NOT_ACTIVITY = new Set(['auth.state'])

/**
 * The company the page expects, on each call of an operation of the session's working
 * organization (ADR 0008 §6.2): a check the server makes against the session's, never where the
 * organization comes from. A tab showing another company than the session's gets 409
 * `organization-changed`, and nothing is done.
 */
export const EXPECTED_ORGANIZATION_HEADER = 'x-eklevo-organization'

/** The options of a call about the company the page shows: `client.x.mutate(input, forCompany(id))`. */
export function forCompany(organizationId: string) {
  return { context: { organizationId } }
}

function expectedOrganization(context: Record<string, unknown>): Record<string, string> {
  const id = context.organizationId
  return typeof id === 'string' ? { [EXPECTED_ORGANIZATION_HEADER]: id } : {}
}

function operationOf(input: RequestInfo | URL): string {
  const href = input instanceof Request ? input.url : String(input)
  return new URL(href, window.location.origin).pathname.slice(STAFF_API.length + 1)
}

export function createStaffQueryClient() {
  // A request refused for lack of a session: the tab must not keep showing anything. A refusal
  // that only asks for the passkey again (`reauthenticate`) is the re-authentication dialog's.
  const onError = (err: unknown) => {
    if (sessionGone(err)) endStaffSession()
  }
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failures, err) => failures < 1 && !sessionGone(err) && !needsReauthentication(err),
        refetchOnWindowFocus: false,
      },
    },
  })
}

export function createStaffApiClient() {
  return createTRPCClient<StaffRouter>({
    links: [
      httpLink({
        url: STAFF_API,
        methodOverride: 'POST',
        headers: ({ op }) => expectedOrganization(op.context),
        fetch: async (input, init) => {
          const res = await fetch(input, { ...init, credentials: 'same-origin' })
          if (res.ok && !NOT_ACTIVITY.has(operationOf(input))) recordActivity()
          return res
        },
      }),
    ],
  })
}
